import { useEffect, useMemo, useRef, useState } from 'react';
import { DndContext, PointerSensor, closestCenter, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, arrayMove, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Button, Divider, Input, Message, Modal, Panel, Stack, toaster } from 'rsuite';

import {
  useCreateWbsTemplate,
  useCreateWbsTemplateItem,
  useDeleteWbsTemplate,
  useDeleteWbsTemplateItem,
  useReorderWbsTemplateItems,
  useUpdateWbsTemplate,
  useUpdateWbsTemplateItem,
  useWbsTemplateItems,
  useWbsTemplates,
} from '../hooks/useWbs';

const idsFromItems = (list) =>
  (Array.isArray(list) ? list : []).map((i) => Number(i?.id)).filter((id) => Number.isInteger(id));

const toPositiveIntOrNull = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const n = Number(value);
  if (!Number.isInteger(n) || n <= 0) return null;
  return n;
};

const SortableWbsItem = ({ item, idx, onEditItem, onDeleteItem }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: Number(item.id) });

  return (
    <div
      ref={setNodeRef}
      style={{
        border: '1px solid #e5e5ea',
        borderRadius: 10,
        padding: 10,
        display: 'grid',
        gridTemplateColumns: '1fr auto',
        gap: 10,
        alignItems: 'start',
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.7 : 1,
      }}
    >
      <div style={{ minWidth: 0, textAlign: 'left' }}>
        <div style={{ fontWeight: 700, lineHeight: '18px', wordBreak: 'break-word', textAlign: 'left' }}>
          <span
            style={{
              color: '#666',
              fontSize: 12,
              fontWeight: 800,
              fontVariantNumeric: 'tabular-nums',
              marginRight: 6,
              textAlign: 'left',
            }}
            aria-label={`Item ${idx + 1}`}
            title={`Item ${idx + 1}`}
          >
            {idx + 1}.
          </span>
          {item.title}
        </div>
        {item?.assumptions ? (
          <div style={{ marginTop: 6, fontSize: 12, color: '#666', whiteSpace: 'pre-wrap', textAlign: 'left' }}>{item.assumptions}</div>
        ) : null}
      </div>

      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
        <Button appearance="subtle" size="xs" {...attributes} {...listeners} title="Drag to reorder" aria-label="Drag to reorder">
          <i className="bi bi-grip-vertical" aria-hidden="true" />
        </Button>
        <Button appearance="subtle" size="xs" onClick={() => onEditItem(item)}>
          <i className="bi bi-pencil" aria-hidden="true" />
        </Button>
        <Button appearance="subtle" color="red" size="xs" onClick={() => onDeleteItem(item)}>
          <i className="bi bi-x-circle" aria-hidden="true" />
        </Button>
      </div>
    </div>
  );
};

export default function WbsManagerPage() {
  const { data: templates = [], isLoading: templatesLoading, error: templatesError } = useWbsTemplates();

  const [selectedTemplateId, setSelectedTemplateId] = useState(null);

  const normalizedSelectedTemplateId = useMemo(() => toPositiveIntOrNull(selectedTemplateId), [selectedTemplateId]);

  const selectedTemplate = useMemo(() => {
    if (normalizedSelectedTemplateId === null) return null;
    return (templates || []).find((t) => Number(t?.id) === normalizedSelectedTemplateId) || null;
  }, [templates, normalizedSelectedTemplateId]);

  useEffect(() => {
    if (normalizedSelectedTemplateId !== null) return;
    if (Array.isArray(templates) && templates.length > 0) {
      setSelectedTemplateId(Number(templates[0].id));
    }
  }, [templates, normalizedSelectedTemplateId]);

  const {
    data: items = [],
    isLoading: itemsLoading,
    error: itemsError,
  } = useWbsTemplateItems(normalizedSelectedTemplateId, { enabled: normalizedSelectedTemplateId !== null });

  const createTemplate = useCreateWbsTemplate({
    onSuccess: (t) => {
      toaster.push(
        <Message type="success" showIcon>
          Template created.
        </Message>,
        { placement: 'topEnd', duration: 2500 }
      );
      setSelectedTemplateId(Number(t?.id));
    },
  });

  const updateTemplate = useUpdateWbsTemplate({
    onSuccess: () => {
      toaster.push(
        <Message type="success" showIcon>
          Template updated.
        </Message>,
        { placement: 'topEnd', duration: 2500 }
      );
    },
  });

  const deleteTemplate = useDeleteWbsTemplate({
    onSuccess: () => {
      toaster.push(
        <Message type="success" showIcon>
          Template deleted.
        </Message>,
        { placement: 'topEnd', duration: 2500 }
      );
      setSelectedTemplateId(null);
    },
  });

  const createItem = useCreateWbsTemplateItem({
    onSuccess: () => {
      toaster.push(
        <Message type="success" showIcon>
          Item added.
        </Message>,
        { placement: 'topEnd', duration: 2000 }
      );
    },
  });

  const updateItem = useUpdateWbsTemplateItem({
    onSuccess: () => {
      toaster.push(
        <Message type="success" showIcon>
          Item updated.
        </Message>,
        { placement: 'topEnd', duration: 2000 }
      );
    },
  });

  const deleteItem = useDeleteWbsTemplateItem({
    onSuccess: () => {
      toaster.push(
        <Message type="success" showIcon>
          Item deleted.
        </Message>,
        { placement: 'topEnd', duration: 2000 }
      );
    },
  });

  const reorderItems = useReorderWbsTemplateItems({
    onSuccess: () => {
      toaster.push(
        <Message type="success" showIcon>
          Order saved.
        </Message>,
        { placement: 'topEnd', duration: 1500 }
      );
    },
  });

  const [createOpen, setCreateOpen] = useState(false);
  const [createName, setCreateName] = useState('');
  const [createNotes, setCreateNotes] = useState('');

  const [editOpen, setEditOpen] = useState(false);
  const [editName, setEditName] = useState('');
  const [editNotes, setEditNotes] = useState('');

  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

  const [addItemOpen, setAddItemOpen] = useState(false);
  const [addItemTitle, setAddItemTitle] = useState('');
  const [addItemAssumptions, setAddItemAssumptions] = useState('');

  const [confirmDeleteItemOpen, setConfirmDeleteItemOpen] = useState(false);
  const [deleteItemTarget, setDeleteItemTarget] = useState(null);

  const [editItemOpen, setEditItemOpen] = useState(false);
  const [editItemId, setEditItemId] = useState(null);
  const [editItemTitle, setEditItemTitle] = useState('');
  const [editItemAssumptions, setEditItemAssumptions] = useState('');
  const [draftItems, setDraftItems] = useState([]);

  const unsavedOrderWarningMessage = 'You have unsaved item order changes. Leave without saving?';
  const previousTemplateIdRef = useRef(null);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  const hasUnsavedOrderChanges = useMemo(() => {
    const draftIds = idsFromItems(draftItems);
    const serverIds = idsFromItems(items);
    if (draftIds.length !== serverIds.length) return false;
    for (let i = 0; i < draftIds.length; i += 1) {
      if (draftIds[i] !== serverIds[i]) return true;
    }
    return false;
  }, [draftItems, items]);

  useEffect(() => {
    const handler = (e) => {
      if (!hasUnsavedOrderChanges) return;
      e.preventDefault();
      e.returnValue = '';
    };

    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [hasUnsavedOrderChanges]);

  useEffect(() => {
    if (!hasUnsavedOrderChanges) return undefined;

    const handler = (e) => {
      const target = e.target;
      if (!(target instanceof Element)) return;

      const anchor = target.closest('a[href]');
      if (!anchor) return;

      const hrefAttr = anchor.getAttribute('href');
      if (!hrefAttr || hrefAttr.startsWith('#')) return;
      if (anchor.getAttribute('target') === '_blank') return;
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;

      const nextUrl = new URL(anchor.href, window.location.href);
      const currentUrl = new URL(window.location.href);
      const isSameUrl =
        nextUrl.pathname === currentUrl.pathname &&
        nextUrl.search === currentUrl.search &&
        nextUrl.hash === currentUrl.hash;
      if (isSameUrl) return;

      const ok = window.confirm(unsavedOrderWarningMessage);
      if (!ok) {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    document.addEventListener('click', handler, true);
    return () => document.removeEventListener('click', handler, true);
  }, [hasUnsavedOrderChanges, unsavedOrderWarningMessage]);

  useEffect(() => {
    const templateId = normalizedSelectedTemplateId;
    const previousTemplateId = previousTemplateIdRef.current;
    const templateChanged = templateId !== previousTemplateId;
    const serverItems = Array.isArray(items) ? items : [];

    if (templateChanged) {
      setDraftItems((prev) => {
        const prevIds = idsFromItems(prev);
        const nextIds = idsFromItems(serverItems);
        if (prevIds.length === nextIds.length && prevIds.every((id, idx) => id === nextIds[idx])) return prev;
        return serverItems;
      });
      previousTemplateIdRef.current = templateId;
      return;
    }

    setDraftItems((prev) => {
      if (!Array.isArray(prev) || prev.length === 0) {
        const prevIds = idsFromItems(prev);
        const nextIds = idsFromItems(serverItems);
        if (prevIds.length === nextIds.length && prevIds.every((id, idx) => id === nextIds[idx])) return prev;
        return serverItems;
      }
      const byId = new Map(serverItems.map((item) => [Number(item?.id), item]));
      const merged = [];
      const used = new Set();

      for (const prevItem of prev) {
        const id = Number(prevItem?.id);
        if (!Number.isInteger(id)) continue;
        const latest = byId.get(id);
        if (!latest) continue;
        merged.push(latest);
        used.add(id);
      }

      for (const serverItem of serverItems) {
        const id = Number(serverItem?.id);
        if (!Number.isInteger(id) || used.has(id)) continue;
        merged.push(serverItem);
      }

      const prevIds = idsFromItems(prev);
      const mergedIds = idsFromItems(merged);
      if (prevIds.length === mergedIds.length && prevIds.every((id, idx) => id === mergedIds[idx])) {
        return prev;
      }

      return merged;
    });
  }, [items, normalizedSelectedTemplateId]);

  const openCreate = () => {
    setCreateName('');
    setCreateNotes('');
    setCreateOpen(true);
  };

  const submitCreate = async () => {
    const name = String(createName || '').trim();
    if (!name) {
      toaster.push(
        <Message type="warning" showIcon>
          Name is required.
        </Message>,
        { placement: 'topEnd', duration: 2500 }
      );
      return;
    }
    await createTemplate.mutateAsync({ name, notes: createNotes });
    setCreateOpen(false);
  };

  const openEdit = () => {
    if (!selectedTemplate) return;
    setEditName(selectedTemplate?.name || '');
    setEditNotes(selectedTemplate?.notes || '');
    setEditOpen(true);
  };

  const submitEdit = async () => {
    if (!selectedTemplate) return;
    const name = String(editName || '').trim();
    if (!name) {
      toaster.push(
        <Message type="warning" showIcon>
          Name is required.
        </Message>,
        { placement: 'topEnd', duration: 2500 }
      );
      return;
    }
    await updateTemplate.mutateAsync({ templateId: selectedTemplate.id, payload: { name, notes: editNotes } });
    setEditOpen(false);
  };

  const submitDeleteTemplate = async () => {
    if (!selectedTemplate) return;
    await deleteTemplate.mutateAsync(selectedTemplate.id);
    setConfirmDeleteOpen(false);
  };

  const submitAddItem = async () => {
    if (!selectedTemplate) return;
    const title = String(addItemTitle || '').trim();
    if (!title) return;
    await createItem.mutateAsync({
      templateId: selectedTemplate.id,
      payload: { title, assumptions: String(addItemAssumptions || '').trim() },
    });
    setAddItemTitle('');
    setAddItemAssumptions('');
    setAddItemOpen(false);
  };

  const openEditItem = (item) => {
    const itemId = Number(item?.id);
    if (!Number.isInteger(itemId)) return;
    setEditItemId(itemId);
    setEditItemTitle(item?.title || '');
    setEditItemAssumptions(item?.assumptions || '');
    setEditItemOpen(true);
  };

  const closeEditItem = () => {
    setEditItemOpen(false);
    setEditItemId(null);
    setEditItemTitle('');
    setEditItemAssumptions('');
  };

  const submitEditItem = async () => {
    if (!selectedTemplate) return;
    const itemId = Number(editItemId);
    if (!Number.isInteger(itemId)) return;
    const title = String(editItemTitle || '').trim();
    if (!title) return;
    await updateItem.mutateAsync({
      templateId: selectedTemplate.id,
      itemId,
      payload: {
        title,
        assumptions: String(editItemAssumptions || '').trim(),
      },
    });
    closeEditItem();
  };

  const openDeleteItem = (item) => {
    const itemId = Number(item?.id);
    if (!Number.isInteger(itemId)) return;
    setDeleteItemTarget(item);
    setConfirmDeleteItemOpen(true);
  };

  const submitDeleteItem = async () => {
    if (!selectedTemplate) return;
    const itemId = Number(deleteItemTarget?.id);
    if (!Number.isInteger(itemId)) return;
    await deleteItem.mutateAsync({ templateId: selectedTemplate.id, itemId });
    setConfirmDeleteItemOpen(false);
    setDeleteItemTarget(null);
  };

  const submitSaveOrder = async () => {
    if (!selectedTemplate) return;
    if (!hasUnsavedOrderChanges) return;
    const itemIds = idsFromItems(draftItems);
    await reorderItems.mutateAsync({ templateId: selectedTemplate.id, itemIds });
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over) return;
    const activeId = Number(active.id);
    const overId = Number(over.id);
    if (!Number.isInteger(activeId) || !Number.isInteger(overId) || activeId === overId) return;

    setDraftItems((prev) => {
      const oldIndex = prev.findIndex((item) => Number(item?.id) === activeId);
      const newIndex = prev.findIndex((item) => Number(item?.id) === overId);
      if (oldIndex < 0 || newIndex < 0) return prev;
      return arrayMove(prev, oldIndex, newIndex);
    });
  };

  const selectTemplate = (templateId) => {
    if (hasUnsavedOrderChanges) {
      const ok = window.confirm(unsavedOrderWarningMessage);
      if (!ok) return;
    }
    setSelectedTemplateId(Number(templateId));
  };

  return (
    <div className="ppc-page">
      <Stack justifyContent="space-between" alignItems="center" wrap>
        <h3 style={{ margin: 0 }}>WBS Manager</h3>
        <Button appearance="primary" onClick={openCreate}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <i className="bi bi-plus-lg" aria-hidden="true" />
            New template
          </span>
        </Button>
      </Stack>

      <Divider />

      {templatesError ? (
        <Message type="error" showIcon>
          Failed to load templates.
        </Message>
      ) : null}

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(260px, 360px) 1fr',
          gap: 16,
          alignItems: 'start',
        }}
      >
        <Panel bordered header={<span style={{ fontWeight: 700 }}>Templates</span>}>
          {templatesLoading ? <div style={{ color: '#666' }}>Loading…</div> : null}
          {Array.isArray(templates) && templates.length === 0 && !templatesLoading ? (
            <div style={{ color: '#666' }}>No templates yet.</div>
          ) : null}

          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {(templates || []).map((t) => {
              const isActive = Number(t?.id) === Number(selectedTemplateId);
              return (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => selectTemplate(Number(t.id))}
                  style={{
                    textAlign: 'left',
                    border: `1px solid ${isActive ? 'rgba(0,115,116,0.35)' : '#e5e5ea'}`,
                    background: isActive ? 'rgba(0,115,116,0.08)' : '#fff',
                    padding: '10px 12px',
                    borderRadius: 10,
                    cursor: 'pointer',
                  }}
                >
                  <div style={{ fontWeight: 700, marginBottom: 4, lineHeight: '18px' }}>{t.name}</div>
                  {t.notes ? (
                    <div style={{ color: '#666', fontSize: 12, lineHeight: '16px' }}>{String(t.notes).slice(0, 90)}</div>
                  ) : (
                    <div style={{ color: '#999', fontSize: 12 }}>No notes</div>
                  )}
                </button>
              );
            })}
          </div>
        </Panel>

        <Panel
          bordered
          header={
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
              <span style={{ fontWeight: 700 }}>Items</span>
              <div style={{ display: 'flex', gap: 8 }}>
                <Button appearance="ghost" size="sm" disabled={!selectedTemplate} onClick={openEdit}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                    <i className="bi bi-pencil" aria-hidden="true" />
                    Edit
                  </span>
                </Button>
                <Button
                  appearance="ghost"
                  size="sm"
                  color="red"
                  disabled={!selectedTemplate}
                  onClick={() => setConfirmDeleteOpen(true)}
                >
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                    <i className="bi bi-trash" aria-hidden="true" />
                    Delete
                  </span>
                </Button>
              </div>
            </div>
          }
        >
          {!selectedTemplate ? <div style={{ color: '#666' }}>Select a template to manage its items.</div> : null}

          {selectedTemplate ? (
            <>
              <div style={{ marginBottom: 10 }}>
                <div style={{ fontWeight: 800, fontSize: 16 }}>{selectedTemplate.name}</div>
                <div style={{ color: '#666', marginTop: 4 }}>{selectedTemplate.notes || '—'}</div>
              </div>

              {itemsError ? (
                <Message type="error" showIcon>
                  Failed to load items.
                </Message>
              ) : null}

              {itemsLoading ? <div style={{ color: '#666' }}>Loading…</div> : null}

              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                <SortableContext items={idsFromItems(draftItems)} strategy={verticalListSortingStrategy}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {draftItems.map((item, idx) => (
                      <SortableWbsItem
                        key={item.id}
                        item={item}
                        idx={idx}
                        onEditItem={openEditItem}
                        onDeleteItem={openDeleteItem}
                      />
                    ))}
                  </div>
                </SortableContext>
              </DndContext>

              <Divider />

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                {hasUnsavedOrderChanges ? (
                  <Button appearance="primary" color="green" onClick={submitSaveOrder} loading={reorderItems.isPending}>
                    Save order
                  </Button>
                ) : null}
                <Button appearance="primary" onClick={() => {
                  setAddItemTitle('');
                  setAddItemAssumptions('');
                  setAddItemOpen(true);
                }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                    <i className="bi bi-plus-lg" aria-hidden="true" />
                    Add new
                  </span>
                </Button>
              </div>
            </>
          ) : null}
        </Panel>
      </div>

      <Modal open={addItemOpen} onClose={() => setAddItemOpen(false)} size="sm">
        <Modal.Header>
          <Modal.Title>Add Item</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div>
              <div style={{ fontWeight: 700, marginBottom: 6 }}>Title</div>
              <Input
                placeholder="e.g. Kickoff meeting"
                value={addItemTitle}
                onChange={setAddItemTitle}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') submitAddItem();
                }}
              />
            </div>
            <div>
              <div style={{ fontWeight: 700, marginBottom: 6 }}>Assumptions</div>
              <Input
                as="textarea"
                rows={5}
                placeholder="Optional"
                value={addItemAssumptions}
                onChange={setAddItemAssumptions}
              />
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button appearance="primary" onClick={submitAddItem} loading={createItem.isPending} disabled={!String(addItemTitle || '').trim()}>
            Add
          </Button>
          <Button appearance="subtle" onClick={() => setAddItemOpen(false)}>
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        open={editItemOpen}
        onClose={() => setEditItemOpen(false)}
        size="sm"
      >
        <Modal.Header>
          <Modal.Title>Edit Item</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div>
              <div style={{ fontWeight: 700, marginBottom: 6 }}>Title</div>
              <Input
                value={editItemTitle}
                onChange={setEditItemTitle}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') submitEditItem();
                  if (e.key === 'Escape') closeEditItem();
                }}
              />
            </div>
            <div>
              <div style={{ fontWeight: 700, marginBottom: 6 }}>Assumptions</div>
              <Input as="textarea" rows={5} placeholder="Optional" value={editItemAssumptions} onChange={setEditItemAssumptions} />
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button appearance="primary" onClick={submitEditItem} loading={updateItem.isPending}>
            Save
          </Button>
          <Button appearance="subtle" onClick={closeEditItem}>
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        open={confirmDeleteItemOpen}
        onClose={() => {
          setConfirmDeleteItemOpen(false);
          setDeleteItemTarget(null);
        }}
        size="xs"
      >
        <Modal.Header>
          <Modal.Title>Delete item?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Message type="warning" showIcon>
            This cannot be undone.
          </Message>
          <div style={{ marginTop: 10, fontSize: 13 }}>
            Delete <b>{deleteItemTarget?.title || 'this item'}</b>?
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button appearance="primary" color="red" onClick={submitDeleteItem} loading={deleteItem.isPending}>
            Delete
          </Button>
          <Button
            appearance="subtle"
            onClick={() => {
              setConfirmDeleteItemOpen(false);
              setDeleteItemTarget(null);
            }}
            disabled={deleteItem.isPending}
          >
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal open={createOpen} onClose={() => setCreateOpen(false)} size="sm">
        <Modal.Header>
          <Modal.Title>New WBS Template</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div>
              <div style={{ fontWeight: 700, marginBottom: 6 }}>Name</div>
              <Input value={createName} onChange={setCreateName} />
            </div>
            <div>
              <div style={{ fontWeight: 700, marginBottom: 6 }}>Notes</div>
              <Input as="textarea" rows={4} value={createNotes} onChange={setCreateNotes} />
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button appearance="primary" onClick={submitCreate} loading={createTemplate.isPending}>
            Create
          </Button>
          <Button appearance="subtle" onClick={() => setCreateOpen(false)}>
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal open={editOpen} onClose={() => setEditOpen(false)} size="sm">
        <Modal.Header>
          <Modal.Title>Edit Template</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div>
              <div style={{ fontWeight: 700, marginBottom: 6 }}>Name</div>
              <Input value={editName} onChange={setEditName} />
            </div>
            <div>
              <div style={{ fontWeight: 700, marginBottom: 6 }}>Notes</div>
              <Input as="textarea" rows={4} value={editNotes} onChange={setEditNotes} />
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button appearance="primary" onClick={submitEdit} loading={updateTemplate.isPending}>
            Save
          </Button>
          <Button appearance="subtle" onClick={() => setEditOpen(false)}>
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal open={confirmDeleteOpen} onClose={() => setConfirmDeleteOpen(false)} size="xs">
        <Modal.Header>
          <Modal.Title>Delete template?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Message type="warning" showIcon>
            This will delete the template and all its items.
          </Message>
        </Modal.Body>
        <Modal.Footer>
          <Button appearance="primary" color="red" onClick={submitDeleteTemplate} loading={deleteTemplate.isPending}>
            Delete
          </Button>
          <Button appearance="subtle" onClick={() => setConfirmDeleteOpen(false)}>
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
